import React, { useEffect, useMemo, useState } from 'react';
import {
    Box,
    Typography,
    TextField,
    Divider,
    IconButton,
    Button,
    InputAdornment,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    CircularProgress,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import EditIcon from '../../../assets/icons/Edit.png';
import CloseIcon from '../../../assets/icons/Close.png';
import AntSwitch from '../../UIControls/AntSwitch';
import PinkGradientButton from '../../UIControls/PinkGradientButton';
import ConfirmationDialog from '../../UIControls/ConfirmationDialog';
import UserApi from '../../../rest-api/user-api';

const SettingsIntegrations = ({
    clinicianData,
    idToken,
    onRefresh,
    isFreeplan = false,
    onUpgradeClick = () => {},
    isMultiUser = false,
    inviteId = null,
    emailAddress = '',
    currentUserId = '',
}) => {
    const [editorActive, setEditorActive] = useState(false);
    const [integrationEnabled, setIntegrationEnabled] = useState(false);
    const [apiKey, setApiKey] = useState('');
    const [showApiKey, setShowApiKey] = useState(false);
    const [status, setStatus] = useState({ state: 'idle', message: 'Not connected' });
    const [statusMessage, setStatusMessage] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [removeDialogOpen, setRemoveDialogOpen] = useState(false);
    const [helpDialogOpen, setHelpDialogOpen] = useState(false);

    const existingIntegration = useMemo(() => {
        if (!clinicianData) return null;
        if (isMultiUser) {
            const multiuser = clinicianData.multiuserData?.multiuserUsers?.find(
                (user) => user.userId === currentUserId,
            );
            return multiuser?.integrations?.cliniko || null;
        }
        return clinicianData.integrations?.cliniko || null;
    }, [clinicianData, isMultiUser, currentUserId]);

    useEffect(() => {
        if (!editorActive) {
            const storedKey = existingIntegration?.apiKey || '';
            setApiKey(storedKey);
            setIntegrationEnabled(Boolean(storedKey));
        }
    }, [existingIntegration?.apiKey, editorActive]);

    useEffect(() => {
        let isActive = true;
        const storedKey = existingIntegration?.apiKey;
        if (!storedKey || !idToken) {
            setStatus({ state: 'disconnected', message: 'Not connected' });
            return () => {
                isActive = false;
            };
        }

        setStatus({ state: 'checking', message: 'Checking connection...' });
        UserApi.validateClinikoKey(idToken, storedKey)
            .then((response) => {
                if (!isActive) return;
                if (response?.valid) {
                    setStatus({ state: 'connected', message: 'Connected' });
                } else {
                    setStatus({ state: 'disconnected', message: 'Not connected' });
                }
            })
            .catch(() => {
                if (!isActive) return;
                setStatus({ state: 'error', message: 'Unable to validate connection' });
            });

        return () => {
            isActive = false;
        };
    }, [existingIntegration?.apiKey, idToken]);

    const handleToggleEdit = () => {
        if (isFreeplan) {
            onUpgradeClick();
            return;
        }
        setEditorActive(!editorActive);
    };

    const handleToggleIntegration = (event) => {
        if (isFreeplan) {
            onUpgradeClick();
            return;
        }
        const checked = event.target.checked;
        if (!checked && existingIntegration?.apiKey) {
            setRemoveDialogOpen(true);
            return;
        }
        setIntegrationEnabled(checked);
        if (checked && !editorActive) {
            setEditorActive(true);
        }
    };

    const handleConnect = async () => {
        if (isFreeplan) {
            onUpgradeClick();
            return;
        }

        const trimmedKey = apiKey.trim();
        if (!trimmedKey) {
            setStatusMessage('Please paste your Cliniko API key.');
            return;
        }

        setIsSubmitting(true);
        setStatusMessage('');
        try {
            const validationResponse = await UserApi.validateClinikoKey(idToken, trimmedKey);
            if (!validationResponse?.valid) {
                setStatus({ state: 'disconnected', message: 'Not connected' });
                setStatusMessage('Cliniko API key is invalid.');
                return;
            }

            const payload = {
                integrations: {
                    cliniko: {
                        apiKey: trimmedKey,
                        userId: validationResponse.userId || null,
                        shard: validationResponse.shard || null,
                    },
                },
            };

            if (isMultiUser) {
                await UserApi.updateClinicianDataMultiuser(idToken, {
                    ...payload,
                    action: 'updateMultiuser',
                    inviteId,
                    emailAddress,
                });
            } else {
                await UserApi.updateClinicianData(idToken, payload);
            }

            setStatus({ state: 'connected', message: 'Connected' });
            setIntegrationEnabled(true);
            setEditorActive(false);
            if (onRefresh) {
                await onRefresh();
            }
        } catch (error) {
            if (error?.response?.status === 403) {
                onUpgradeClick();
            } else {
                setStatusMessage(error?.message || 'Unable to connect Cliniko.');
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleRemoveConfirmed = async () => {
        setRemoveDialogOpen(false);
        setIsSubmitting(true);
        setStatusMessage('');
        try {
            const payload = {
                integrations: {
                    cliniko: {
                        apiKey: '',
                    },
                },
            };

            if (isMultiUser) {
                await UserApi.updateClinicianDataMultiuser(idToken, {
                    ...payload,
                    action: 'updateMultiuser',
                    inviteId,
                    emailAddress,
                });
            } else {
                await UserApi.updateClinicianData(idToken, payload);
            }

            setApiKey('');
            setIntegrationEnabled(false);
            setStatus({ state: 'disconnected', message: 'Not connected' });
            setEditorActive(false);
            if (onRefresh) {
                await onRefresh();
            }
        } catch (error) {
            setStatusMessage(error?.message || 'Unable to remove Cliniko integration.');
        } finally {
            setIsSubmitting(false);
        }
    };

    const statusColor =
        status.state === 'connected'
            ? '#2E7D32'
            : status.state === 'error'
              ? '#C62828'
              : status.state === 'checking'
                ? '#6b6b6b'
                : '#616063';

    return (
        <Box
            className="settings-block"
            sx={{
                backgroundColor: 'white',
                borderRadius: 4,
                padding: '16px 24px',
                my: 3,
            }}
        >
            <Box
                sx={{
                    position: 'relative',
                    mb: 2,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                }}
            >
                <Typography variant="h5" sx={{ fontWeight: 600, height: 48 }}>
                    Integrations
                </Typography>
                <IconButton onClick={handleToggleEdit} sx={{ p: 0 }}>
                    {editorActive ? <img src={CloseIcon} alt="Close" /> : <img src={EditIcon} alt="Edit" />}
                </IconButton>
                <Divider
                    sx={{
                        width: 'calc(100% + 48px)',
                        position: 'absolute',
                        bottom: 0,
                        left: '-24px',
                        backgroundColor: 'rgba(229, 229, 229, 1)',
                        mt: 2,
                    }}
                />
            </Box>

            <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                Cliniko Integration (Beta)
            </Typography>
            <Typography variant="body2" sx={{ color: '#616063', mb: 2 }}>
                Please paste the copied Cliniko API key here.
            </Typography>

            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    Enable Cliniko integration
                </Typography>
                <AntSwitch checked={integrationEnabled} onChange={handleToggleIntegration} />
            </Box>

            <Typography variant="body2" sx={{ color: statusColor, mb: 2 }}>
                {status.state === 'checking' ? 'Checking connection...' : status.message}
            </Typography>

            {integrationEnabled && (
                <Box sx={{ mb: 2 }}>
                    <TextField
                        fullWidth
                        type={showApiKey ? 'text' : 'password'}
                        value={apiKey}
                        placeholder="Cliniko API key"
                        onChange={(e) => setApiKey(e.target.value)}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton onClick={() => setShowApiKey((prev) => !prev)} edge="end">
                                        {showApiKey ? <VisibilityOff /> : <Visibility />}
                                    </IconButton>
                                </InputAdornment>
                            ),
                            readOnly: !editorActive,
                            style: {
                                background: editorActive ? '#F7F7F7' : 'transparent',
                            },
                        }}
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                '& fieldset': {
                                    border: 'none',
                                },
                            },
                            mb: 1,
                        }}
                    />

                    {statusMessage && (
                        <Typography variant="body2" sx={{ color: '#C62828', mb: 2 }}>
                            {statusMessage}
                        </Typography>
                    )}

                    {editorActive && (
                        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                            <PinkGradientButton onClick={handleConnect} disabled={isSubmitting}>
                                {isSubmitting ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : 'Connect'}
                            </PinkGradientButton>
                            {existingIntegration?.apiKey && (
                                <Button variant="outlined" color="error" onClick={() => setRemoveDialogOpen(true)}>
                                    Remove
                                </Button>
                            )}
                        </Box>
                    )}
                </Box>
            )}

            <Button variant="text" onClick={() => setHelpDialogOpen(true)} sx={{ pl: 0 }}>
                How to find your Cliniko API key
            </Button>

            <Dialog open={helpDialogOpen} onClose={() => setHelpDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Cliniko integration setup</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" sx={{ color: '#616063', mb: 2 }}>
                        Step 1: Log in to your Cliniko account. Click <strong>My info</strong> in the lower‑left corner,
                        then scroll down to <strong>Manage API keys</strong>.
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#616063', mb: 2 }}>
                        Step 2: Click <strong>Add an API key</strong>. Give it a name (e.g., “Cetra Health”) and press{' '}
                        <strong>Create API key</strong>.
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#616063', mb: 2 }}>
                        Step 3: Copy the new API key (it’s shown only once). Paste it into the Cliniko integration field
                        here and press <strong>Connect</strong>.
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#616063' }}>
                        Note: Each practitioner should create their own API key.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setHelpDialogOpen(false)}>Close</Button>
                </DialogActions>
            </Dialog>

            <ConfirmationDialog
                open={removeDialogOpen}
                handleClose={() => setRemoveDialogOpen(false)}
                onConfirm={handleRemoveConfirmed}
                title="Remove Cliniko integration?"
                message="Are you sure you want to remove your Cliniko API key from Cetra?"
                confirmText="Remove"
                cancelText="Cancel"
            />
        </Box>
    );
};

export default SettingsIntegrations;
